import { movies } from '@/mocks/movies';
import { newsList } from '@/mocks/news';
import { specials } from '@/mocks/specials';
import type { Movie, NewsItem, SpecialItem } from '@/types';

export const allMovies: Movie[] = movies;
export const allNews: NewsItem[] = newsList;

export function getMovieById(id?: string): Movie | undefined {
  return allMovies.find((movie) => movie.id === id);
}

export function getNewsById(id?: string): NewsItem | undefined {
  return allNews.find((item) => item.id === id);
}

export function getSpecialById(id?: string): SpecialItem | undefined {
  return specials.find((item) => item.id === id);
}

export function getMoviesByIds(ids: string[]): Movie[] {
  return ids.map((id) => getMovieById(id)).filter((movie): movie is Movie => Boolean(movie));
}

export function getFeaturedMovies(): Movie[] {
  return allMovies.filter((movie) => movie.featured);
}

export function getTrendingMovies(count = 6): Movie[] {
  return [...allMovies].sort((a, b) => b.popularity - a.popularity).slice(0, count);
}

export function getLatestMovies(count = 8): Movie[] {
  return [...allMovies]
    .sort((a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime())
    .slice(0, count);
}

export function getTopRatedMovies(count = 6): Movie[] {
  return [...allMovies].sort((a, b) => b.rating - a.rating).slice(0, count);
}

export function getRelatedMovies(movie: Movie, count = 4): Movie[] {
  const scored = allMovies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      const genreScore = item.genres.filter((genre) => movie.genres.includes(genre)).length;
      return { item, score: genreScore * 10 + item.rating };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, count).map((entry) => entry.item);
}

export function getRelatedNews(news: NewsItem, count = 3): NewsItem[] {
  return allNews
    .filter((item) => item.id !== news.id)
    .sort((a, b) => {
      const aScore = a.category === news.category ? 1 : 0;
      const bScore = b.category === news.category ? 1 : 0;
      return bScore - aScore;
    })
    .slice(0, count);
}

export function getGenresFromMovies(): string[] {
  const set = new Set<string>();
  allMovies.forEach((movie) => movie.genres.forEach((genre) => set.add(genre)));
  return Array.from(set);
}

export interface MovieFilters {
  keyword?: string;
  genres?: string[];
  year?: string;
  region?: string;
  minRating?: number;
  sort?: 'latest' | 'rating' | 'popular';
}

export function filterMovies(filters: MovieFilters): Movie[] {
  const { keyword = '', genres: selectedGenres = [], year = '全部年份', region = '全部地区', minRating = 0, sort = 'latest' } = filters;

  const normalized = keyword.trim().toLowerCase();

  const result = allMovies.filter((movie) => {
    const matchKeyword =
      !normalized ||
      movie.title.toLowerCase().includes(normalized) ||
      movie.enTitle.toLowerCase().includes(normalized) ||
      movie.director.toLowerCase().includes(normalized) ||
      movie.cast.some((person) => person.toLowerCase().includes(normalized)) ||
      movie.genres.some((genre) => genre.includes(normalized));

    const matchGenre = selectedGenres.length === 0 || selectedGenres.some((genre) => movie.genres.includes(genre));
    const matchYear = year === '全部年份' || String(movie.year) === year;
    const matchRegion = region === '全部地区' || movie.region === region;
    const matchRating = movie.rating >= minRating;

    return matchKeyword && matchGenre && matchYear && matchRegion && matchRating;
  });

  if (sort === 'rating') {
    return result.sort((a, b) => b.rating - a.rating);
  }
  if (sort === 'popular') {
    return result.sort((a, b) => b.popularity - a.popularity);
  }
  return result.sort((a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime());
}

export function searchMovies(keyword: string): Movie[] {
  return filterMovies({ keyword, sort: 'popular' });
}

export function searchNews(keyword: string): NewsItem[] {
  const normalized = keyword.trim().toLowerCase();
  if (!normalized) return [];
  return allNews.filter(
    (item) =>
      item.title.toLowerCase().includes(normalized) ||
      item.subtitle.toLowerCase().includes(normalized) ||
      item.excerpt.toLowerCase().includes(normalized) ||
      item.category.toLowerCase().includes(normalized),
  );
}

export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} 分钟`;
  return `${hours} 小时 ${rest} 分`;
}

export function formatDate(date: string): string {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
}

export function getAllTags(limit = 12): string[] {
  const set = new Set<string>();
  allMovies.forEach((movie) => movie.tags.forEach((tag) => set.add(tag)));
  return Array.from(set).slice(0, limit);
}